import axios, { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import { HttpResponse } from 'interfaces/httpClient';
import {
	AdminPanelTableData,
	CasesByAdminLawyer,
	LawyerDetail,
	LawyerDetailCaseList,
	LawyerRoles,
	NotificationsPayload,
	PasswordPayload,
	PersonalDetailsI,
	RegisterLawyerPaylod,
	UserI,
} from 'interfaces';

/**
 * This class should hold all the routes to the endpoints that are
 * related to user handling inside the app.
 *
 * Note: Routes might change depending on the project
 * so its needed to check those before using it
 */
class UserLawyerService {
	constructor(private client: AxiosInstance) {}

	/* Personal Information */

	async updatePersonalInformation(
		data: PersonalDetailsI
	): Promise<HttpResponse<{ message: string }>> {
		const formData = new FormData();
		if (data?.doc) {
			formData.append('doc', data.doc);
		}
		if (data?.phoneNumber) {
			formData.append('phoneNumber', String(data.phoneNumber));
		}
		if (data?.additionalEmail) {
			formData.append('additionalEmail', data.additionalEmail);
		}
		if (data.removeProfilePicture) {
			formData.append(
				'removeProfilePicture',
				String(data.removeProfilePicture)
			);
		}

		formData.append('firstName', data.firstName);
		formData.append('lastName', data.lastName);

		data.address1 && formData.append('address1', data.address1);
		data.address2 && formData.append('address2', data.address2);
		data.city && formData.append('city', data.city);
		data.state && formData.append('state', data.state);
		data.zipCode && formData.append('zipCode', String(data.zipCode));
		formData.append('receiveUpdates', String(data.receiveUpdates));

		return this.client.post('/user-lawyer/me/personal-information', formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
	}

	async updatePassword(data: PasswordPayload): Promise<HttpResponse<UserI>> {
		return this.client.put('/user-lawyer/change-password', data);
	}
	async updateNotificationsPreferences(
		data: NotificationsPayload
	): Promise<HttpResponse<UserI>> {
		return this.client.put('/user-lawyer/notification-preferences', data);
	}
	/**
	 * Update notification preferences using an explicit Bearer token instead of the logged-in session.
	 * Useful for flows like email unsubscribe links where the user might not be logged in.
	 */
	async updateNotificationsPreferencesWithId(
		userId: string,
		data: NotificationsPayload
	): Promise<HttpResponse<UserI>> {
		const client = axios.create({
			baseURL: process.env.NEXT_PUBLIC_API,
		});

		const response = await client.post<HttpResponse<UserI>>(
			`/auth/update-notifications-options/${userId}`,
			data
		);
		return response.data;
	}

	//-------- Admin Panel ------------//
	async getLawyerRolesList(): Promise<HttpResponse<LawyerRoles[]>> {
		return this.client.get('user-lawyer/lawyer-roles-list');
	}
	async getTeamMembers(
		page: number,
		limit: number,
		search?: string,
		role_ids?: number[]
	): Promise<HttpResponse<AdminPanelTableData>> {
		const params = {
			page,
			limit,
			...(search && { search: search.toLowerCase() }),
			...(role_ids && { role_ids }),
		};
		return this.client.get(`/user-lawyer/team-member-list`, { params });
	}

	async getLawyerDetail(id: string): Promise<HttpResponse<LawyerDetail>> {
		return this.client.get(`/user-lawyer/lawyer-personal-info/${id}`);
	}

	async getLawyerCases(
		id: string,
		pageParam: number,
		phase: string,
		status: string,
		search: string
	): Promise<HttpResponse<LawyerDetailCaseList>> {
		const params = {
			page: pageParam,
			limit: 6,
			...(phase && { phase_id: phase }),
			...(status && { status_id: status }),
			...(search && { search }),
		};

		return this.client.get(`/user-lawyer/lawyer-cases-list/${id}`, { params });
	}

	async getValidateLawyerEmail(
		email: string
	): Promise<HttpResponse<{ messages: string; value: boolean }>> {
		return this.client.get(`/user-lawyer/validate-lawyer-email/${email}`);
	}

	async postRegisterLawyer(
		lawyers: RegisterLawyerPaylod[]
	): Promise<HttpResponse<LawyerDetail>> {
		return this.client.post('/user-lawyer/register-lawyers', { lawyers });
	}

	async postResendInvitationLawyer(
		user_id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/user-lawyer/send-invitation/${user_id}`);
	}

	async updateRoleLawyer(
		user_id: string,
		role_id: number
	): Promise<HttpResponse<LawyerDetail>> {
		return this.client.put('/user-lawyer/update-role-lawyer', {
			user_id,
			role_id,
		});
	}

	async deactivateLawyerAccount(
		user_id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(`/user-lawyer/deactivate-lawyer/${user_id}`);
	}

	async deleteLawyerAccount(
		user_id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(`/user-lawyer/delete-lawyer/${user_id}`);
	}

	async reactivateLawyerAccount(
		user_id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.put(`/user-lawyer/reactivate-lawyer/${user_id}`);
	}

	async asyncListLawyerCases(
		lawyer_id: string
	): Promise<HttpResponse<CasesByAdminLawyer[]>> {
		return this.client.get(`/user-lawyer/cases-lawyer-delete/${lawyer_id}`);
	}

	async updateLawyerEmail(user_id: string, email: string): Promise<any> {
		return this.client.put(`/user-lawyer/update-email-lawyer`, {
			user_id,
			email,
		});
	}
}

export const userLawyerService = new UserLawyerService(axiosClient);
